import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import { Target, Globe, Award, ArrowRight, ChevronDown } from "lucide-react";
import BlogCardDesign3 from "../components/blog/BlogCardDesign3";
import AutoSlider from "../components/reusable/AutoSlider";
import ContactUsHome from "../components/homepage/ContactUsHome";
import blogData from "../blogs.json";
import { namedClients } from "../utils/clients";
import SuccessStoryEmbed from "../components/trust/SuccessStoryEmbed";
import { useState } from "react";

const Navbar = dynamic(() => import("../components/Navbar"), { ssr: false });
const Footer = dynamic(() => import("../components/Footer"));

// NOTE: positional — must match the case-study order in blogs.json (date desc).
// Realign this array whenever a case-study post is added/removed.
const BADGE_TITLES = [
  "Enterprise Search & Market Intelligence", // enterprise-search-case-study-business-research-company
  "Retail Technology Management",        // d2c-retail-technology-management-takeover
  "Financial Services",                 // fintech-technology-operations-turnaround
  "Platform Engineering",               // reusable-microservices-faster-software-delivery (featured)
  "Market Intelligence & Web Operations", // website-optimization-security-market-intelligence
  "Financial Services",                 // fintech-audit-tech-stacks-strategy
  "Supply Chain & AI",                  // supply-chain-ai-image-matching
  "Financial Services",                 // fintech-transformation-legacy-startups
  "Startup & Investment Tech",          // startup-investing-entrepreneurs-investors
  "Hyperlocal Delivery & E-commerce",   // retail-tech-enhancements-delivery
  "Logistics & ERP Solutions",          // logistics-supply-chain-efficiency
  "Risk Management & Business Intelligence", // business-intelligence-ai-strategy
  "Apparel & E-Commerce",               // d2c-apparel-digital-operations
  "Chemical & Manufacturing Technology", // chemical-formulations-contract-manufacturing
];

export async function getStaticProps() {
  let caseStudyIndex = 0;
  const sortedData = blogData
    .map(item => {
      if (item?.meta?.category !== "case-study") return null;
      return {
        title: item.meta.title,
        img: item.meta.img,
        slug: item.meta.slug,
        date: item.meta.date,
        author: item.meta.author,
        subtitle: item.meta.subtitle,
        badge: BADGE_TITLES[caseStudyIndex++] || "",
      };
    })
    .filter(Boolean);

  const selected = blogData
    .filter(item => !["case-study", "tech"].includes(item?.meta?.category))
    .slice(0, 5);

  return { props: { sortedData, selected } };
}

const CAPABILITY_SECTIONS = [
  { referTag: "Data and Artificial Intelligence",      heading: "AI and data engineering" },
  { referTag: "Enterprise Applications and Automation", heading: "Enterprise applications and automation" },
  { referTag: "Cyber Security",                        heading: "Cyber security and risk" },
];

export default function SuccessStories({ sortedData, selected }) {
  const [openCapability, setOpenCapability] = useState(null);
  const featured = sortedData.find(
    post => post.slug === "reusable-microservices-faster-software-delivery"
  );
  const remainingStories = sortedData.filter(post => post.slug !== featured?.slug);

  return (
    <div className="blog bg-white">
      <Head>
        <title>Customer Success Stories | ITMTB</title>
        <meta
          name="description"
          content="Explore real-world case studies where ITMTB helped clients in fintech, logistics, manufacturing, and retail solve complex tech challenges and scale successfully."
        />
        <meta
          name="keywords"
          content="customer success stories, ITMTB case studies, fintech solutions, supply chain tech, business transformation, enterprise technology, AI implementation, digital innovation"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Customer Success Stories | ITMTB" />
        <meta
          property="og:description"
          content="Real-world examples of how ITMTB delivered cutting-edge solutions in payments, AI, logistics, manufacturing, and more."
        />
        <meta property="og:image" content="https://www.itmtb.com/logo.png" />
        <meta property="og:url" content="https://www.itmtb.com/success-stories" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Customer Success Stories | ITMTB" />
        <meta name="twitter:description" content="See how ITMTB empowered fintechs, manufacturers, e-commerce startups, and global enterprises through innovative tech solutions." />
        <meta name="twitter:image" content="https://www.itmtb.com/logo.png" />
        <link rel="canonical" href="https://www.itmtb.com/success-stories" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: "Customer Success Stories | ITMTB",
              description: "Real-world examples of how ITMTB helped companies across fintech, supply chain, AI, and manufacturing solve complex tech problems and scale operations.",
              url: "https://www.itmtb.com/success-stories",
              publisher: {
                "@type": "Organization",
                name: "ITMTB",
                logo: { "@type": "ImageObject", url: "https://www.itmtb.com/logo.png" },
              },
            }),
          }}
        />
      </Head>

      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-10 bg-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Transforming Businesses
            <span className="block text-[#F1AA4E]">Across Industries</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Discover how we've partnered with forward-thinking organisations to deliver measurable results and drive innovation across diverse sectors.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4" />
              <span>Proven Impact Across Industries</span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4" />
              <span>16 Industries</span>
            </div>
            <div className="flex items-center space-x-2">
              <Award className="w-4 h-4" />
              <span>Proven Results</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured spotlight */}
      {featured && (
        <section className="pb-16 pt-10 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="bg-[#F1AA4E] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 inline-block">
                Featured Success Story
              </span>
              <h2 className="text-3xl font-bold text-gray-900">Spotlight</h2>
            </div>
            <div className="flex flex-col lg:grid lg:grid-cols-2 bg-white shadow-2xl rounded-3xl overflow-hidden">
              <Link href={`/blog/${featured.slug}`} passHref>
                <div className="relative h-64 lg:h-full w-full cursor-pointer">
                  <Image src={featured.img} alt={featured.title} width={800} height={600} className="w-full h-full object-cover" priority />
                </div>
              </Link>
              <div className="p-6 lg:p-8 flex flex-col justify-between space-y-4">
                <Link href={`/blog/${featured.slug}`} passHref>
                  <div className="cursor-pointer">
                    <p className="text-md text-gray-500 mb-2">Platform Engineering</p>
                    <h3 className="text-2xl font-bold text-gray-900 leading-snug">{featured.title}</h3>
                    <p className="text-md text-gray-600">{featured.subtitle}</p>
                  </div>
                </Link>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center space-y-1">
                    <div className="text-lg font-bold text-[#F1AA4E]">~18%</div>
                    <div className="text-xs text-gray-600 font-semibold">Lower Cost Per Project</div>
                    <div className="text-xs text-gray-600">Saved across code, testing, and docs on every custom build.</div>
                  </div>
                  <div className="text-center space-y-1">
                    <div className="text-lg font-bold text-[#F1AA4E]">7,000+</div>
                    <div className="text-xs text-gray-600 font-semibold">Test Cases Inherited</div>
                    <div className="text-xs text-gray-600">Hardening every new product carries from day one.</div>
                  </div>
                  <div className="text-center space-y-1">
                    <div className="text-lg font-bold text-[#F1AA4E]">5</div>
                    <div className="text-xs text-gray-600 font-semibold">Products, One Foundation</div>
                    <div className="text-xs text-gray-600">12 reusable modules powering five live products.</div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Link href={`/blog/${featured.slug}`} passHref>
                    <button className="bg-[#F1AA4E] text-white text-sm px-4 py-2 rounded-lg flex items-center space-x-1 hover:bg-[#e19940] transition cursor-pointer">
                      <span>Read Full Case Study</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All case studies grid */}
      <section className="text-black flex flex-col w-full px-4 sm:px-8 mb-12">
        <div className="mt-16 rounded-sm flex flex-col items-start max-w-7xl mx-auto w-full">
          <h2 className="text-2xl sm:text-4xl md:text-5xl mb-5 font-semibold text-gray-800">All Success Stories</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
            {remainingStories.map(post => (
              <div key={post.slug} className="w-full">
                <BlogCardDesign3 img={post.img} title={post.title} subheading={post.subtitle} slug={post.slug} badge={post.badge} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse by capability — collapsible */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-2">Browse by capability</h2>
        <p className="text-gray-500 text-sm mb-6">The delivery stack behind each engagement.</p>
        <div className="divide-y divide-gray-200 border border-gray-200 rounded-xl overflow-hidden">
          {CAPABILITY_SECTIONS.map(({ referTag, heading }) => {
            const isOpen = openCapability === referTag;
            return (
              <div key={referTag}>
                <button
                  onClick={() => setOpenCapability(isOpen ? null : referTag)}
                  className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-gray-50 transition-colors text-left"
                >
                  <span className="font-medium text-gray-800">{heading}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-gray-100">
                    <SuccessStoryEmbed referTag={referTag} maxItems={3} heading="" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Named clients */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-16">
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-2">Our Clients</h2>
        <p className="text-gray-500 text-sm mb-8">Organisations we've built and shipped for.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {namedClients.map(client => (
            <a
              key={client.name}
              href={client.url !== "#" ? client.url : undefined}
              target={client.url !== "#" ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="group border border-gray-200 rounded-xl p-4 hover:border-[#F1AA4E] hover:shadow-sm transition-all flex items-center gap-3"
            >
              {client.logo ? (
                <div className={`w-12 h-10 flex-shrink-0 rounded-lg p-1 flex items-center justify-center ${client.darkBg ? "bg-gray-800" : "bg-gray-50"}`}>
                  <img src={client.logo} alt={client.name} className="object-contain max-w-full max-h-full" />
                </div>
              ) : (
                <div className="w-10 h-10 flex-shrink-0 bg-amber-50 rounded-lg flex items-center justify-center">
                  <span className="text-[#F1AA4E] font-bold text-base">{client.name.charAt(0)}</span>
                </div>
              )}
              <span className="text-xs font-medium text-gray-800 group-hover:text-[#F1AA4E] transition-colors truncate">
                {client.name}
              </span>
            </a>
          ))}
        </div>
      </section>

      <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-gray-800 text-center">Trending Now</h2>
      <AutoSlider posts={selected} />
      <hr className="my-8 border-gray-200" />
      <section className="text-center py-10 px-6">
        <p className="text-gray-500 text-sm mb-3">Seen enough? Want to know how we actually engage?</p>
        <Link href="/itmtb-how-we-work">
          <a className="inline-block border border-gray-300 hover:border-blue-500 text-gray-700 hover:text-blue-600 font-semibold px-7 py-3 rounded-lg transition-colors text-sm">
            How we work →
          </a>
        </Link>
      </section>
      <ContactUsHome />
      <Footer />
    </div>
  );
}

